import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Controller, useWatch, type Control, type UseFormSetValue } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import {
  ACHIEVEMENT_TYPES_QUERY,
  type MembershipAchievementCategoryEnum,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import { applyAchievementTypeSelection, buildAchievementTypeOptions, clearAchievementTypeSelection } from "../utils";
import type { AchievementTypeOption, EditAchievementFormValues } from "../types";

const SEARCH_DEBOUNCE_MS = 300;

type AchievementTypeFieldProps = {
  category: MembershipAchievementCategoryEnum;
  categoryLabel: string;
  control: Control<EditAchievementFormValues>;
  disabled: boolean;
  initialTypeLabel: string;
  isAddMode: boolean;
  onAchievementTypesChange(achievementTypes: MembershipAchievementType[]): void;
  onLoadingChange(loading: boolean): void;
  open: boolean;
  setValue: UseFormSetValue<EditAchievementFormValues>;
};

export default function AchievementTypeField({
  category,
  categoryLabel,
  control,
  disabled,
  initialTypeLabel,
  isAddMode,
  onAchievementTypesChange,
  onLoadingChange,
  open,
  setValue
}: AchievementTypeFieldProps) {
  const isSectionScoped = useIsSectionScopedForEvents();
  const [query, setQuery] = useState(initialTypeLabel);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const selectedCode = useWatch({ control, name: "code" });
  const currentTitle = useWatch({ control, name: "title" });

  useEffect(() => {
    setQuery(initialTypeLabel);
    setDebouncedSearchTerm("");
  }, [initialTypeLabel]);

  useEffect(() => {
    if (selectedCode) {
      setDebouncedSearchTerm("");
      return;
    }

    const handler = setTimeout(() => setDebouncedSearchTerm(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [query, selectedCode]);

  const search = selectedCode ? undefined : debouncedSearchTerm.trim() || undefined;
  const { data, loading } = useQuery(ACHIEVEMENT_TYPES_QUERY, {
    variables: {
      category,
      ...(isAddMode && isSectionScoped ? { canSectionsManage: true } : {}),
      ...(search ? { search } : {})
    },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const options = useMemo(() => buildAchievementTypeOptions(data?.achievementTypes), [data?.achievementTypes]);

  useEffect(() => {
    onLoadingChange(loading);
    onAchievementTypesChange(data?.achievementTypes ?? []);
  }, [data?.achievementTypes, loading, onAchievementTypesChange, onLoadingChange]);

  const clearQuery = () => {
    setQuery("");
    setDebouncedSearchTerm("");
  };

  return (
    <Controller
      name="code"
      control={control}
      rules={{ required: `${categoryLabel} type is required` }}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete<AchievementTypeOption>
          variant="searchableDropdown"
          label={category === "AWARD" ? "Award Type" : "Achievement Type"}
          data={options}
          query={query}
          loadingResults={loading}
          required
          disabled={disabled}
          error={!!error}
          errorText={error?.message}
          placeholder={`Search ${categoryLabel.toLowerCase()} types`}
          data-testid={`${categoryLabel.toLowerCase()}-type`}
          leadingIcon="search"
          constrainMenuWidth
          menuClassName="max-h-[300px]"
          renderItem={(option) => (
            <div className="flex flex-col">
              <div className="text-body-large">{option.label}</div>
              <div className="text-label-medium text-sys-on-surface-variant">
                {option.native ? "Native" : option.value}
              </div>
            </div>
          )}
          onChangeText={(text) => {
            setQuery(text);
            if (value) {
              clearAchievementTypeSelection(onChange, setValue);
            }
          }}
          reset={() => {
            clearQuery();
            clearAchievementTypeSelection(onChange, setValue);
          }}
          onSelect={(option) => {
            applyAchievementTypeSelection({
              currentTitle: currentTitle ?? "",
              onChange,
              option,
              options,
              previousCode: selectedCode ?? "",
              setValue
            });
            setQuery(option.label);
            setDebouncedSearchTerm("");
          }}
        />
      )}
    />
  );
}
